import type { OrganizationMetadata } from "@repo/auth";
import { authClient } from "@repo/auth/client";
import type {
	DataExportFileSummary,
	DataExportScope,
	PortalDomainDto,
} from "@repo/core/client";
import { apiClient } from "@shared/lib/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PortalDomainRequestError } from "./portal-domain-errors";

export const organizationListQueryKey = ["user", "organizations"] as const;
export const useOrganizationListQuery = () => {
	return useQuery({
		queryKey: organizationListQueryKey,
		queryFn: async () => {
			const { data, error } = await authClient.organization.list();

			if (error) {
				throw new Error(
					error.message || "Failed to fetch organizations",
				);
			}

			return data;
		},
	});
};

export const activeOrganizationQueryKey = (slug: string) =>
	["user", "activeOrganization", slug] as const;
export const useActiveOrganizationQuery = (
	slug: string,
	options?: {
		enabled?: boolean;
	},
) => {
	return useQuery({
		queryKey: activeOrganizationQueryKey(slug),
		queryFn: async () => {
			const { data, error } =
				await authClient.organization.getFullOrganization({
					query: {
						organizationSlug: slug,
					},
				});

			if (error) {
				throw new Error(
					error.message || "Failed to fetch active organization",
				);
			}

			return data;
		},
		enabled: options?.enabled,
	});
};

export const fullOrganizationQueryKey = (id: string) =>
	["fullOrganization", id] as const;
export const useFullOrganizationQuery = (
	id: string,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: fullOrganizationQueryKey(id),
		queryFn: async () => {
			const { data, error } =
				await authClient.organization.getFullOrganization({
					query: {
						organizationId: id,
					},
				});

			if (error) {
				throw new Error(
					error.message || "Failed to fetch full organization",
				);
			}

			return data;
		},
		enabled: options?.enabled ?? true,
	});
};

/*
 * Fetch organization with all custom fields (subscriptionPlanType, logoType, etc.)
 * This bypasses better-auth limitations and fetches directly from our database
 */
export const organizationDetailsQueryKey = (id: string) =>
	["organizationDetails", id] as const;

export const useOrganizationDetails = (
	id: string | undefined,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: organizationDetailsQueryKey(id || ""),
		queryFn: async () => {
			if (!id) {
				throw new Error("Organization ID is required");
			}

			const response = await apiClient.organizations.details[":id"].$get({
				param: { id },
			});

			if (!response.ok) {
				throw new Error("Failed to fetch organization details");
			}

			return await response.json();
		},
		enabled: !!id && (options?.enabled ?? true),
	});
};

export const generateOrganizationSlug = async (name: string) => {
	const response = await apiClient.organizations["generate-slug"].$get({
		query: {
			name,
		},
	});

	if (!response.ok) {
		throw new Error("Failed to generate organization slug");
	}

	const { slug } = await response.json();

	return slug;
};

/*
 * Create organization
 */
export const createOrganizationMutationKey = ["create-organization"] as const;
export const useCreateOrganizationMutation = () => {
	return useMutation({
		mutationKey: createOrganizationMutationKey,
		mutationFn: async ({
			name,
			metadata,
		}: { name: string; metadata?: OrganizationMetadata }) =>
			(
				await authClient.organization.create({
					name,
					slug: await generateOrganizationSlug(name),
					metadata,
				})
			).data,
	});
};

/*
 * Update organization
 */
export const updateOrganizationMutationKey = ["update-organization"] as const;
export const useUpdateOrganizationMutation = () => {
	return useMutation({
		mutationKey: updateOrganizationMutationKey,
		mutationFn: async ({
			id,
			name,
			metadata,
			updateSlug,
		}: {
			id: string;
			name: string;
			metadata?: OrganizationMetadata;
			updateSlug?: boolean;
		}) =>
			(
				await authClient.organization.update({
					organizationId: id,
					data: {
						name,
						slug: updateSlug
							? await generateOrganizationSlug(name)
							: undefined,
						metadata,
					},
				})
			).data,
	});
};

/*
 * Credit availability check
 */
export interface CreditCheckResult {
	canProceed: boolean;
	isLegacyPlan: boolean;
	hasCredits: boolean;
	availableBalance: number;
	requiredCredits: number;
	willCauseOverage: boolean;
	isPayAsYouGo: boolean;
	message?: string;
}

export const creditCheckQueryKey = (
	organizationId: string,
	credits: number,
	propertyId?: string,
) =>
	[
		"organization",
		organizationId,
		"credits",
		"check",
		credits,
		propertyId,
	] as const;

export const useCheckCreditAvailability = (
	organizationId: string,
	credits: number,
	options?: {
		enabled?: boolean;
		propertyId?: string;
	},
) => {
	return useQuery({
		queryKey: creditCheckQueryKey(
			organizationId,
			credits,
			options?.propertyId,
		),
		queryFn: async () => {
			const response = await apiClient.organizations["credit-check"][
				":id"
			].$get({
				param: { id: organizationId },
				query: {
					credits: credits.toString(),
					propertyId: options?.propertyId,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to check credit availability");
			}

			return (await response.json()) as CreditCheckResult;
		},
		enabled: options?.enabled,
		// Don't cache this query - always fetch fresh data
		staleTime: 0,
		gcTime: 0,
	});
};

/*
 * AI Visibility Report billing check
 * Every report costs credits (pure credit model); this returns the cost and
 * subscriber status for messaging.
 */
export type AiReportBillingReason = "credits_required";

export interface AiReportBillingResult {
	isFree: boolean;
	reason: AiReportBillingReason;
	requiredCredits: number;
	isActiveSubscriber: boolean;
}

export const aiReportBillingQueryKey = (
	organizationId: string,
	propertyId: string,
) => ["organization", organizationId, "ai-report-billing", propertyId] as const;

export const useAiReportBilling = (
	organizationId: string,
	propertyId: string,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: aiReportBillingQueryKey(organizationId, propertyId),
		queryFn: async () => {
			const response = await apiClient.organizations["ai-report-billing"][
				":id"
			].$get({
				param: { id: organizationId },
				query: { propertyId },
			});

			if (!response.ok) {
				throw new Error("Failed to check AI report billing");
			}

			return (await response.json()) as AiReportBillingResult;
		},
		enabled: options?.enabled,
		staleTime: 0,
		gcTime: 0,
	});
};

/*
 * Fetch organization branding from domain
 */
export const fetchBrandingMutationKey = ["fetch-branding"] as const;
export const useFetchBrandingMutation = () => {
	return useMutation({
		mutationKey: fetchBrandingMutationKey,
		mutationFn: async ({
			organizationId,
			domain,
		}: { organizationId: string; domain: string }) => {
			const response = await apiClient.organizations[":id"][
				"fetch-branding"
			].$post({
				param: { id: organizationId },
				json: { domain },
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(
					(errorData as any).error || "Failed to fetch brand data",
				);
			}

			return await response.json();
		},
	});
};

/*
 * Update organization branding fields
 */
export const updateBrandingMutationKey = ["update-branding"] as const;
export const useUpdateBrandingMutation = () => {
	return useMutation({
		mutationKey: updateBrandingMutationKey,
		mutationFn: async ({
			organizationId,
			logo,
			logoType,
		}: {
			organizationId: string;
			logo?: string | null;
			logoType?: string | null;
		}) => {
			const response = await apiClient.organizations[
				":id"
			].branding.$patch({
				param: { id: organizationId },
				json: { logo, logoType },
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(
					(errorData as any).error || "Failed to update branding",
				);
			}

			return await response.json();
		},
	});
};

/*
 * Organization plan information
 */
export const organizationPlanQueryKey = (id: string) =>
	["organization", "plan", id] as const;

export interface PlanInfo {
	planId: string | null;
	planCategory: string;
	isActive: boolean;
	subscriptionStatus: string | null;
	/** "month" | "quarter" | "year" for a paid subscription. */
	subscriptionInterval: string | null;
	isBrand: boolean;
	isAgency: boolean;
	isEnterprise: boolean;
	isLegacy: boolean;
	isTrial: boolean;
	isFree: boolean;
	isPayAsYouGo: boolean;
	hasActiveSubscription: boolean;
	hasTrialSubscription: boolean;
	hasExpiredTrial: boolean;
	planTier: string | null;
	/** Admin-enabled trial tools, e.g. "youtube_optimizer". */
	enabledTools?: string[];
}

export const useOrganizationPlan = (
	id: string | undefined,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: organizationPlanQueryKey(id || ""),
		queryFn: async () => {
			if (!id) {
				throw new Error("Organization ID is required");
			}

			const response = await apiClient.organizations.plan[":id"].$get({
				param: { id },
			});

			if (!response.ok) {
				throw new Error("Failed to fetch organization plan");
			}

			return (await response.json()) as PlanInfo;
		},
		enabled: !!id && (options?.enabled ?? true),
		staleTime: 5 * 60 * 1000, // Cache for 5 minutes (plan doesn't change often)
	});
};

/*
 * Update domain auto-join setting
 */
export const updateDomainAutoJoinMutationKey = [
	"update-domain-auto-join",
] as const;
export const useUpdateDomainAutoJoinMutation = () => {
	return useMutation({
		mutationKey: updateDomainAutoJoinMutationKey,
		mutationFn: async ({
			organizationId,
			allowDomainAutoJoin,
		}: { organizationId: string; allowDomainAutoJoin: boolean }) => {
			const response = await apiClient.organizations[":id"][
				"domain-auto-join"
			].$patch({
				param: { id: organizationId },
				json: { allowDomainAutoJoin },
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(
					(errorData as any).error ||
						"Failed to update domain auto-join setting",
				);
			}

			return await response.json();
		},
	});
};

/*
 * Update org-wide report section defaults (which optional sections new reports
 * include by default). Properties without a per-property override inherit this.
 */
export interface ReportSectionDefaults {
	showTechnicalAudit: boolean;
	showGroundingSearches: boolean;
	showBrandConsistency: boolean;
}
export const updateReportSectionDefaultsMutationKey = [
	"update-report-section-defaults",
] as const;
export const useUpdateReportSectionDefaultsMutation = () => {
	return useMutation({
		mutationKey: updateReportSectionDefaultsMutationKey,
		mutationFn: async ({
			organizationId,
			...defaults
		}: { organizationId: string } & ReportSectionDefaults) => {
			const response = await apiClient.organizations[":id"][
				"report-section-defaults"
			].$patch({
				param: { id: organizationId },
				json: defaults,
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(
					(errorData as any).error ||
						"Failed to update report section defaults",
				);
			}

			return await response.json();
		},
	});
};

/*
 * Require two-factor authentication for every member of the organization.
 *
 * Owner-only on the server. `requireMfa` is read back from
 * `/organizations/details/:id` rather than better-auth's full-organization
 * payload, which only carries the columns better-auth itself declares.
 */
export const updateRequireMfaMutationKey = ["update-require-mfa"] as const;
export const useUpdateRequireMfaMutation = () => {
	return useMutation({
		mutationKey: updateRequireMfaMutationKey,
		mutationFn: async ({
			organizationId,
			requireMfa,
		}: { organizationId: string; requireMfa: boolean }) => {
			const response = await apiClient.organizations[":id"][
				"require-mfa"
			].$patch({
				param: { id: organizationId },
				json: { requireMfa },
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(
					(errorData as any).error ||
						"Failed to update the two-factor requirement",
				);
			}

			return await response.json();
		},
	});
};

/*
 * Update domain URL for member auto-join
 */
export const updateDomainUrlMutationKey = ["update-domain-url"] as const;
export const useUpdateDomainUrlMutation = () => {
	return useMutation({
		mutationKey: updateDomainUrlMutationKey,
		mutationFn: async ({
			organizationId,
			domainUrl,
		}: { organizationId: string; domainUrl: string }) => {
			const response = await apiClient.organizations[":id"][
				"domain-url"
			].$patch({
				param: { id: organizationId },
				json: { domainUrl },
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(
					(errorData as any).error || "Failed to update domain URL",
				);
			}

			return await response.json();
		},
	});
};

/*
 * Organization AIVPS API key
 *
 * Note: these routes live under `/organization-api-keys/:id` in a dedicated
 * router rather than inside `/organizations/:id/api-key`, to avoid a Hono
 * trie conflict (mixing dynamic `:id` with static-first GETs in the
 * organizations router would silently break authentication — see CLAUDE.md).
 */
export const organizationApiKeyQueryKey = (id: string) =>
	["organization", id, "api-key"] as const;

export const useOrganizationApiKey = (
	organizationId: string | undefined,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: organizationApiKeyQueryKey(organizationId || ""),
		queryFn: async () => {
			if (!organizationId) {
				throw new Error("Organization ID is required");
			}

			const response = await apiClient["organization-api-keys"][
				":id"
			].$get({
				param: { id: organizationId },
			});

			if (!response.ok) {
				throw new Error("Failed to fetch organization API key");
			}

			return (await response.json()) as { apiKey: string | null };
		},
		enabled: !!organizationId && (options?.enabled ?? true),
		staleTime: 0,
	});
};

export const useGenerateOrganizationApiKeyMutation = () => {
	return useMutation({
		mutationFn: async ({ organizationId }: { organizationId: string }) => {
			const response = await apiClient["organization-api-keys"][
				":id"
			].$post({
				param: { id: organizationId },
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(
					(errorData as any).error || "Failed to generate API key",
				);
			}

			return (await response.json()) as { apiKey: string | null };
		},
	});
};

export const useRevokeOrganizationApiKeyMutation = () => {
	return useMutation({
		mutationFn: async ({ organizationId }: { organizationId: string }) => {
			const response = await apiClient["organization-api-keys"][
				":id"
			].$delete({
				param: { id: organizationId },
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(
					(errorData as any).error || "Failed to revoke API key",
				);
			}

			return (await response.json()) as { success: boolean };
		},
	});
};

/*
 * Organization data export (data portability).
 *
 * The list is polled while a request is queued or building; download links
 * are plain anchors to the API route rather than RPC calls, because that route
 * answers with a 302 to a short-lived signed URL.
 */
export interface DataExportRow {
	id: string;
	status: string;
	createdAt: string;
	completedAt: string | null;
	fileSizeBytes: number | null;
	requestedByEmail: string;
	error: string | null;
	/** What the request asked for; null = everything. */
	scope: DataExportScope | null;
	/**
	 * The delivered files of a completed multi-file export; null for an export
	 * from before multi-file delivery, which has one archive instead.
	 */
	files: DataExportFileSummary[] | null;
}

/** Download URL for an export: one named file, or a legacy export's archive. */
export const dataExportDownloadHref = (
	organizationId: string,
	exportId: string,
	fileName?: string,
) =>
	`/api/organizations/${organizationId}/data-exports/${exportId}/download${
		fileName ? `?file=${encodeURIComponent(fileName)}` : ""
	}`;

/** An export that has not finished one way or the other yet. */
export const isDataExportInProgress = (dataExport: DataExportRow) =>
	dataExport.status === "pending" || dataExport.status === "running";

export const dataExportsQueryKey = (organizationId: string) =>
	["organization", organizationId, "data-exports"] as const;

export const useDataExportsQuery = (
	organizationId: string | undefined,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: dataExportsQueryKey(organizationId || ""),
		queryFn: async () => {
			if (!organizationId) {
				throw new Error("Organization ID is required");
			}

			const response = await apiClient.organizations[":id"][
				"data-exports"
			].$get({
				param: { id: organizationId },
			});

			if (!response.ok) {
				throw new Error("Failed to fetch data exports");
			}

			const { exports } = (await response.json()) as {
				exports: DataExportRow[];
			};

			return exports;
		},
		enabled: !!organizationId && (options?.enabled ?? true),
		// Only poll while something is actually building — an idle settings page
		// should not hold a request open every five seconds.
		refetchInterval: (query) =>
			query.state.data?.some(isDataExportInProgress) ? 5000 : false,
	});
};

export const requestDataExportMutationKey = ["request-data-export"] as const;
export const useRequestDataExportMutation = () => {
	return useMutation({
		mutationKey: requestDataExportMutationKey,
		mutationFn: async ({
			organizationId,
			scope = {},
		}: {
			organizationId: string;
			/** Omitted fields mean "everything"; `{}` is the full export. */
			scope?: DataExportScope;
		}) => {
			const response = await apiClient.organizations[":id"][
				"data-exports"
			].$post({
				param: { id: organizationId },
				json: scope,
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(
					(errorData as any).error || "Failed to request data export",
					{ cause: response.status },
				);
			}

			return await response.json();
		},
	});
};

/**
 * The organization's OIDC single sign-on configuration, minus the client
 * secret — the API never returns it, so the form treats an empty secret field
 * on an already-configured provider as "keep the stored one".
 *
 * `eligible` is the subscription gate: SSO is available on every paid plan but
 * not on pay-as-you-go, and the card renders an upgrade prompt instead of a
 * form when it is false.
 */
export type SsoConfiguration = {
	configured: boolean;
	eligible: boolean;
	providerId: string;
	callbackUrl: string;
	issuer?: string;
	domain?: string;
	clientId?: string | null;
};

export const ssoConfigurationQueryKey = (organizationId: string) =>
	["organization", organizationId, "sso"] as const;

export const useSsoConfigurationQuery = (
	organizationId: string | undefined,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: ssoConfigurationQueryKey(organizationId || ""),
		queryFn: async () => {
			if (!organizationId) {
				throw new Error("Organization ID is required");
			}

			const response = await apiClient.organizations[":id"].sso.$get({
				param: { id: organizationId },
			});

			if (!response.ok) {
				throw new Error("Failed to fetch SSO configuration");
			}

			return (await response.json()) as SsoConfiguration;
		},
		enabled: !!organizationId && (options?.enabled ?? true),
	});
};

export const saveSsoConfigurationMutationKey = [
	"save-sso-configuration",
] as const;
export const useSaveSsoConfigurationMutation = () => {
	return useMutation({
		mutationKey: saveSsoConfigurationMutationKey,
		mutationFn: async ({
			organizationId,
			...body
		}: {
			organizationId: string;
			issuer: string;
			domain: string;
			clientId: string;
			clientSecret?: string;
		}) => {
			const response = await apiClient.organizations[":id"].sso.$put({
				param: { id: organizationId },
				json: body,
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(
					(errorData as any).error ||
						"Failed to save SSO configuration",
					{ cause: (errorData as any).code ?? response.status },
				);
			}

			return (await response.json()) as SsoConfiguration;
		},
	});
};

export const removeSsoConfigurationMutationKey = [
	"remove-sso-configuration",
] as const;
export const useRemoveSsoConfigurationMutation = () => {
	return useMutation({
		mutationKey: removeSsoConfigurationMutationKey,
		mutationFn: async ({ organizationId }: { organizationId: string }) => {
			const response = await apiClient.organizations[":id"].sso.$delete({
				param: { id: organizationId },
			});

			if (!response.ok) {
				const errorData = await response.json();
				throw new Error(
					(errorData as any).error ||
						"Failed to remove SSO configuration",
				);
			}

			return await response.json();
		},
	});
};

/*
 * Agency white-label client portal — the organization's custom hostname.
 */

/**
 * What the settings card renders off.
 *
 * The 402 the API returns for a non-agency plan is a *state*, not a failure:
 * the card shows an upsell for it. Modelling it as a resolved value rather
 * than a rejected query keeps React Query from retrying it and keeps a toast
 * off a page that is behaving exactly as designed.
 */
export type PortalDomainQueryResult =
	| { eligible: false; domain: null; cnameTarget: null }
	| { eligible: true; domain: PortalDomainDto | null; cnameTarget: string };

/**
 * Hono's RPC client types a handler's body as the union of every branch it can
 * return, so a caller that has already narrowed by status has to assert the
 * branch it is holding.
 */
async function readPortalJson<T>(response: {
	json: () => Promise<unknown>;
}): Promise<T> {
	return (await response.json()) as T;
}

/** Turn a non-2xx into an error carrying the API's `error` code. */
async function throwPortalDomainError(
	response: { status: number; json: () => Promise<unknown> },
	fallbackMessage: string,
): Promise<never> {
	let code = "UNKNOWN";
	let message = fallbackMessage;

	try {
		const body = await readPortalJson<{
			error?: string;
			message?: string;
		}>(response);
		code = body.error ?? code;
		message = body.message ?? message;
	} catch {
		// A 502 from an edge/proxy layer need not carry JSON at all.
	}

	throw new PortalDomainRequestError(code, message, response.status);
}

export const portalDomainQueryKey = (organizationId: string) =>
	["organization", organizationId, "portal-domain"] as const;

export const usePortalDomainQuery = (
	organizationId: string | undefined,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: portalDomainQueryKey(organizationId || ""),
		queryFn: async (): Promise<PortalDomainQueryResult> => {
			if (!organizationId) {
				throw new Error("Organization ID is required");
			}

			const response = await apiClient.organizations[":id"][
				"portal-domain"
			].$get({ param: { id: organizationId } });

			if (response.status === 402) {
				return { eligible: false, domain: null, cnameTarget: null };
			}

			if (!response.ok) {
				return await throwPortalDomainError(
					response,
					"Failed to load the portal domain",
				);
			}

			const body = await readPortalJson<{
				domain: PortalDomainDto | null;
				cnameTarget: string;
			}>(response);

			return {
				eligible: true,
				domain: body.domain,
				cnameTarget: body.cnameTarget,
			};
		},
		enabled: !!organizationId && (options?.enabled ?? true),
	});
};

export const attachPortalDomainMutationKey = ["attach-portal-domain"] as const;
export const useAttachPortalDomainMutation = (organizationId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: attachPortalDomainMutationKey,
		mutationFn: async ({ hostname }: { hostname: string }) => {
			const response = await apiClient.organizations[":id"][
				"portal-domain"
			].$put({ param: { id: organizationId }, json: { hostname } });

			if (!response.ok) {
				return await throwPortalDomainError(
					response,
					"Failed to add the portal domain",
				);
			}

			const body = await readPortalJson<{ domain: PortalDomainDto }>(
				response,
			);

			return body.domain;
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: portalDomainQueryKey(organizationId),
			});
		},
	});
};

export const verifyPortalDomainMutationKey = ["verify-portal-domain"] as const;
export const useVerifyPortalDomainMutation = (organizationId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: verifyPortalDomainMutationKey,
		mutationFn: async () => {
			const response = await apiClient.organizations[":id"][
				"portal-domain"
			].verify.$post({ param: { id: organizationId } });

			if (!response.ok) {
				return await throwPortalDomainError(
					response,
					"Failed to re-check the portal domain",
				);
			}

			// A 200 can legitimately carry an ERROR domain — that is the DNS
			// check reporting a real problem, not the request failing.
			const body = await readPortalJson<{ domain: PortalDomainDto }>(
				response,
			);

			return body.domain;
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: portalDomainQueryKey(organizationId),
			});
		},
	});
};

export const removePortalDomainMutationKey = ["remove-portal-domain"] as const;
export const useRemovePortalDomainMutation = (organizationId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: removePortalDomainMutationKey,
		mutationFn: async () => {
			const response = await apiClient.organizations[":id"][
				"portal-domain"
			].$delete({ param: { id: organizationId } });

			if (!response.ok) {
				return await throwPortalDomainError(
					response,
					"Failed to remove the portal domain",
				);
			}

			// 204 — deliberately no body to read.
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: portalDomainQueryKey(organizationId),
			});
		},
	});
};
